import { useState, useEffect, useCallback, useMemo } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Design Tokens (DisclosureOS system) ──────────────────────────
const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96",
  dim: "#384660", subtle: "#1e2d48",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981",
  purple: "#8b5cf6", cyan: "#06b6d4", red: "#ef4444",
  pink: "#ec4899", teal: "#14b8a6", orange: "#f97316",
  lime: "#84cc16", white: "#fff",
};

// ─── EU Taxonomy Regulation Reference Database ────────────────────
const REGULATIONS = {
  "Art. 1": { title: "Subject Matter", desc: "Establishes criteria for determining whether an economic activity qualifies as environmentally sustainable" },
  "Art. 2": { title: "Definitions", desc: "Defines key terms including 'environmentally sustainable economic activity', 'substantial contribution', 'significant harm'" },
  "Art. 3": { title: "Criteria for Environmentally Sustainable Activities", desc: "Activity must: (a) contribute substantially to ≥1 objective, (b) not significantly harm other objectives (DNSH), (c) comply with minimum safeguards, (d) comply with technical screening criteria" },
  "Art. 5": { title: "Transparency — Financial Products", desc: "Pre-contractual disclosures for financial products with environmental sustainability claims" },
  "Art. 8": { title: "Transparency — Undertakings", desc: "Non-financial undertakings must disclose proportion of Turnover, CapEx, OpEx from Taxonomy-eligible and aligned activities" },
  "Art. 9": { title: "Environmental Objectives", desc: "Six environmental objectives: (1) Climate change mitigation, (2) Climate change adaptation, (3) Water and marine resources, (4) Circular economy, (5) Pollution prevention, (6) Biodiversity and ecosystems" },
  "Art. 10": { title: "Substantial Contribution — Climate Mitigation", desc: "Technical screening criteria for substantial contribution to climate change mitigation" },
  "Art. 11": { title: "Substantial Contribution — Climate Adaptation", desc: "Technical screening criteria for substantial contribution to climate change adaptation" },
  "Art. 12": { title: "Substantial Contribution — Water", desc: "Criteria for substantial contribution to sustainable use and protection of water and marine resources" },
  "Art. 13": { title: "Substantial Contribution — Circular Economy", desc: "Criteria for substantial contribution to transition to a circular economy" },
  "Art. 14": { title: "Substantial Contribution — Pollution", desc: "Criteria for substantial contribution to pollution prevention and control" },
  "Art. 15": { title: "Substantial Contribution — Biodiversity", desc: "Criteria for substantial contribution to protection and restoration of biodiversity and ecosystems" },
  "Art. 16": { title: "DNSH — Climate Mitigation", desc: "Criteria for determining that an activity does not significantly harm climate change mitigation" },
  "Art. 17": { title: "DNSH — All Objectives", desc: "Activity significantly harms environmental objectives if it leads to significant GHG emissions, climate impacts, water harm, circular economy harm, pollution increase, or ecosystem harm" },
  "Art. 18": { title: "Minimum Safeguards", desc: "Procedures to ensure alignment with OECD Guidelines, UN Guiding Principles on Business & Human Rights, ILO Core Conventions, International Bill of Human Rights" },
  "Del. Reg. 2021/2139": { title: "Climate Delegated Act", desc: "Technical screening criteria for activities contributing substantially to climate change mitigation or adaptation" },
  "Del. Reg. 2022/1214": { title: "Complementary Delegated Act", desc: "Includes nuclear energy and fossil gas activities under specific conditions" },
  "Del. Reg. 2023/2486": { title: "Environmental Delegated Act", desc: "Technical screening criteria for remaining four environmental objectives (water, circular economy, pollution, biodiversity)" },
  "Del. Reg. 2021/2178": { title: "Disclosures Delegated Act (Art. 8)", desc: "Specifies content, methodology and presentation of information to be disclosed under Article 8 — KPI definitions, templates, reporting formats" },
};

// ─── 6 Environmental Objectives ───────────────────────────────────
const ENV_OBJECTIVES = [
  { id: "ccm", label: "Climate Change Mitigation", icon: "🌡️", color: C.emerald, article: "Art. 10", delegated: "Del. Reg. 2021/2139" },
  { id: "cca", label: "Climate Change Adaptation", icon: "🌊", color: C.cyan, article: "Art. 11", delegated: "Del. Reg. 2021/2139" },
  { id: "wtr", label: "Water & Marine Resources", icon: "💧", color: C.blue, article: "Art. 12", delegated: "Del. Reg. 2023/2486" },
  { id: "ce", label: "Circular Economy", icon: "♻️", color: C.lime, article: "Art. 13", delegated: "Del. Reg. 2023/2486" },
  { id: "pol", label: "Pollution Prevention", icon: "🏭", color: C.amber, article: "Art. 14", delegated: "Del. Reg. 2023/2486" },
  { id: "bio", label: "Biodiversity & Ecosystems", icon: "🌿", color: C.teal, article: "Art. 15", delegated: "Del. Reg. 2023/2486" },
];

// ─── Step Icons & Colors ──────────────────────────────────────────
const STEP_META = {
  scope:     { icon: "🏢", color: C.cyan },
  classify:  { icon: "🏷️", color: C.blue },
  eligible:  { icon: "✅", color: C.emerald },
  align:     { icon: "🎯", color: C.purple },
  calculate: { icon: "📊", color: C.amber },
  disclose:  { icon: "📤", color: C.teal },
};

// ─── Helper Components ────────────────────────────────────────────
const fmtPct = (n) => (n * 100).toFixed(1) + "%";
const fmtEur = (n) => "€" + (n / 1e6).toFixed(1) + "M";

function StatusDot({ status }) {
  const map = { done: C.emerald, active: C.blue, review: C.amber, pending: C.dim, fail: C.red };
  const labels = { done: "Complete", active: "In Progress", review: "Under Review", pending: "Not Started", fail: "Failed" };
  return (
    <span data-testid={`status-dot-${status}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 9, color: map[status] || C.dim }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: map[status] || C.dim, boxShadow: status === "active" ? `0 0 6px ${C.blue}66` : "none" }} />
      {labels[status] || status}
    </span>
  );
}

function Pill({ color, children, onClick, active, small }) {
  return (
    <span onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: small ? "1px 6px" : "3px 9px", borderRadius: 99,
      fontSize: small ? 8 : 10, fontWeight: 600, letterSpacing: 0.2,
      background: active ? color + "22" : color + "10",
      color: color, border: `1px solid ${active ? color + "66" : color + "22"}`,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.2s",
    }}>{children}</span>
  );
}

function Badge({ text, color }) {
  return <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: color || C.amber, color: C.bg, marginLeft: 4, letterSpacing: 0.3 }}>{text}</span>;
}

function ProgressBar({ value, max, color, height = 5 }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ width: "100%", height, borderRadius: 99, background: C.border, overflow: "hidden" }}>
      <div style={{ width: pct + "%", height: "100%", borderRadius: 99, background: `linear-gradient(90deg, ${color}88, ${color})`, transition: "width 0.6s ease" }} />
    </div>
  );
}

function RegRef({ article }) {
  const reg = REGULATIONS[article];
  if (!reg) return <Pill color={C.muted} small>{article}</Pill>;
  return (
    <span title={`${article}: ${reg.title}\n${reg.desc}`}>
      <Pill color={C.cyan} small>{"📜"} {article}</Pill>
    </span>
  );
}

function SectionCard({ title, subtitle, icon, color, children, actions, badge }) {
  return (
    <div style={{ borderRadius: 10, border: `1px solid ${color}22`, background: C.surface, overflow: "hidden", marginBottom: 12 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${color}15`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: 0.3 }}>{title}{badge && <Badge text={badge} color={color} />}</div>
            {subtitle && <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{subtitle}</div>}
          </div>
        </div>
        {actions && <div style={{ display: "flex", gap: 4 }}>{actions}</div>}
      </div>
      <div style={{ padding: "14px 16px" }}>{children}</div>
    </div>
  );
}

// ─── Sub-Views ────────────────────────────────────────────────────

function DashboardView({ activities, kpis, workflowSteps, config }) {
  const eligibleActivities = activities.filter(a => a.eligible);
  const alignedActivities = activities.filter(a => a.aligned === true);
  const reviewActivities = activities.filter(a => a.aligned === "review");

  const totals = kpis?.totals || { turnover: 1, capex: 1, opex: 1 };
  const eligible = kpis?.eligible || { turnover: 0, capex: 0, opex: 0 };
  const aligned = kpis?.aligned || { turnover: 0, capex: 0, opex: 0 };

  const totalTasks = workflowSteps.reduce((s, st) => s + (st.tasks?.length || st.total || 0), 0);
  const doneTasks = workflowSteps.reduce((s, st) => s + (st.tasks?.filter(t => t.status === "done").length || st.done || 0), 0);
  const activeTasks = workflowSteps.reduce((s, st) => s + (st.tasks?.filter(t => t.status === "active").length || 0), 0);

  const KPICard = ({ label, eligNum, alignNum, denom, color, denomLabel }) => (
    <div data-testid={`kpi-card-${label.toLowerCase().replace(/\s/g, '-')}`} style={{ flex: 1, minWidth: 200, padding: "16px 18px", borderRadius: 10, background: C.card, border: `1px solid ${color}22` }}>
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 28, fontWeight: 800, color }}>{denom > 0 ? fmtPct(alignNum / denom) : "0.0%"}</span>
        <span style={{ fontSize: 11, color: C.muted }}>aligned</span>
      </div>
      <ProgressBar value={alignNum} max={denom} color={color} height={6} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10 }}>
        <span style={{ color: C.muted }}>Eligible: <span style={{ color: C.text, fontWeight: 600 }}>{denom > 0 ? fmtPct(eligNum / denom) : "0.0%"}</span></span>
        <span style={{ color: C.muted }}>of {fmtEur(denom)} total</span>
      </div>
      <div style={{ marginTop: 6, fontSize: 9, color: C.dim }}>
        Aligned: {fmtEur(alignNum)} {"·"} Eligible: {fmtEur(eligNum)} {"·"} {denomLabel}: {fmtEur(denom)}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 16 }}>
        <KPICard label="Turnover KPI" eligNum={eligible.turnover} alignNum={aligned.turnover} denom={totals.turnover} color={C.emerald} denomLabel="Revenue" />
        <KPICard label="CapEx KPI" eligNum={eligible.capex} alignNum={aligned.capex} denom={totals.capex} color={C.blue} denomLabel="CapEx" />
        <KPICard label="OpEx KPI" eligNum={eligible.opex} alignNum={aligned.opex} denom={totals.opex} color={C.amber} denomLabel="OpEx" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginBottom: 16 }}>
        {[
          { label: "Total Activities", value: activities.length, sub: "mapped to NACE", color: C.text },
          { label: "Eligible", value: eligibleActivities.length, sub: `of ${activities.length} activities`, color: C.emerald },
          { label: "Aligned", value: alignedActivities.length, sub: `of ${eligibleActivities.length} eligible`, color: C.blue },
          { label: "Under Review", value: reviewActivities.length, sub: "alignment pending", color: C.amber },
          { label: "Workflow Progress", value: `${doneTasks}/${totalTasks}`, sub: `${activeTasks} in progress`, color: C.purple },
        ].map((s, i) => (
          <div key={i} data-testid={`stat-card-${s.label.toLowerCase().replace(/\s/g, '-')}`} style={{ padding: "12px 14px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.text, marginTop: 2 }}>{s.label}</div>
            <div style={{ fontSize: 9, color: C.muted }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <SectionCard title="WORKFLOW PROGRESS" subtitle="6-step Taxonomy implementation" icon={"📋"} color={C.teal}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {workflowSteps.map(step => {
            const done = step.done ?? (step.tasks?.filter(t => t.status === "done").length || 0);
            const total = step.total ?? (step.tasks?.length || 0);
            const meta = STEP_META[step.step_id] || { icon: "", color: C.muted };
            return (
              <div key={step.step_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 7, background: C.card, border: `1px solid ${C.border}` }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: meta.color + "18", border: `1.5px solid ${meta.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: meta.color, flexShrink: 0 }}>{step.num}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>{step.label}</span>
                    <span style={{ fontSize: 9, color: C.muted }}>{"—"} {step.question}</span>
                  </div>
                  <ProgressBar value={done} max={total} color={meta.color} height={4} />
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: done === total ? C.emerald : C.muted, flexShrink: 0 }}>{done}/{total}</div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="ENVIRONMENTAL OBJECTIVES COVERAGE" subtitle="Activities mapped per Art. 9 objective" icon={"🌍"} color={C.emerald}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
          {ENV_OBJECTIVES.map(obj => {
            const count = activities.filter(a => a.objective === obj.id).length;
            return (
              <div key={obj.id} data-testid={`objective-${obj.id}`} style={{ padding: "10px 12px", borderRadius: 7, background: C.card, border: `1px solid ${obj.color}22` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>{obj.icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: obj.color }}>{obj.label}</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: count > 0 ? obj.color : C.dim }}>{count}</div>
                <div style={{ fontSize: 9, color: C.muted }}>activities {"·"} <RegRef article={obj.article} /></div>
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

function ClassificationView({ activities }) {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? activities : filter === "eligible" ? activities.filter(a => a.eligible) : activities.filter(a => !a.eligible);

  return (
    <div>
      <SectionCard title="NACE CODE → TAXONOMY ACTIVITY MAPPING" subtitle="Business activities classified against EU Taxonomy activity list" icon={"🏷️"} color={C.blue}
        actions={
          <div style={{ display: "flex", gap: 4 }}>
            {["all", "eligible", "non-eligible"].map(f => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} style={{
                padding: "3px 9px", borderRadius: 5, fontSize: 9, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${filter === f ? C.blue : C.border}`,
                background: filter === f ? C.blue + "18" : "transparent",
                color: filter === f ? C.blue : C.muted,
              }}>{f === "all" ? `All (${activities.length})` : f === "eligible" ? `Eligible (${activities.filter(a => a.eligible).length})` : `Non-Eligible (${activities.filter(a => !a.eligible).length})`}</button>
            ))}
          </div>
        }>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="classification-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr>
                {["NACE Code", "NACE Description", "Taxonomy Activity", "Objective", "Eligible", "Aligned", "Turnover", "CapEx", "OpEx"].map((h, i) => (
                  <th key={i} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${C.borderHi}`, color: C.muted, fontWeight: 600, fontSize: 9, letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => {
                const obj = ENV_OBJECTIVES.find(o => o.id === a.objective);
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace", fontWeight: 700, color: C.cyan, fontSize: 11 }}>{a.nace}</td>
                    <td style={{ padding: "8px 10px", color: C.text }}>{a.nace_label}</td>
                    <td style={{ padding: "8px 10px", color: a.eligible ? C.text : C.dim, maxWidth: 220, whiteSpace: "normal", lineHeight: 1.4 }}>{a.tax_activity}</td>
                    <td style={{ padding: "8px 10px" }}>{obj ? <Pill color={obj.color} small>{obj.icon} {obj.id.toUpperCase()}</Pill> : <span style={{ color: C.dim }}>{"—"}</span>}</td>
                    <td style={{ padding: "8px 10px" }}>{a.eligible ? <span style={{ color: C.emerald, fontWeight: 700 }}>{"✓"} YES</span> : <span style={{ color: C.dim }}>{"✗"} NO</span>}</td>
                    <td style={{ padding: "8px 10px" }}>
                      {a.aligned === true ? <span style={{ color: C.emerald, fontWeight: 700 }}>{"✓"} YES</span>
                        : a.aligned === "review" ? <span style={{ color: C.amber, fontWeight: 600 }}>{"⏳"} REVIEW</span>
                        : a.aligned === false && a.eligible ? <span style={{ color: C.red }}>{"✗"} NO</span>
                        : <span style={{ color: C.dim }}>{"—"}</span>}
                    </td>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace", color: C.text, textAlign: "right" }}>{fmtEur(a.turnover)}</td>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace", color: C.text, textAlign: "right" }}>{fmtEur(a.capex)}</td>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace", color: C.text, textAlign: "right" }}>{fmtEur(a.opex)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="CLASSIFICATION METHODOLOGY" subtitle="How activities are mapped — auditable decision log" icon={"📐"} color={C.purple}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
          {[
            { step: "1", title: "Extract Activity Register", desc: "All revenue-generating and cost-incurring economic activities are extracted from ERP general ledger, profit centres, and operational systems.", color: C.cyan },
            { step: "2", title: "NACE Rev. 2 Mapping", desc: "Each activity is mapped to its NACE Rev. 2 code using Eurostat NACE classification. Activities may map to multiple NACE codes — each mapping is documented.", color: C.blue },
            { step: "3", title: "Taxonomy Cross-Reference", desc: "NACE codes are cross-referenced against the Taxonomy activity descriptions in the Climate Delegated Act (2021/2139), Complementary Act (2022/1214), and Environmental Act (2023/2486).", color: C.purple },
            { step: "4", title: "Classification Decision", desc: "Each activity is marked as Taxonomy-described (eligible) or not. Activities not listed are non-eligible. Classification rationale is documented with evidence.", color: C.emerald },
            { step: "5", title: "Multi-Mapping Resolution", desc: "Where an activity could map to multiple Taxonomy activities, the most specific match is selected. Enabling vs. transitional classification is applied per Art. 10(1) and 10(2).", color: C.amber },
            { step: "6", title: "Peer Review & Sign-Off", desc: "All classification decisions undergo peer review by Internal Audit. Signed-off classifications become immutable in the audit trail.", color: C.teal },
          ].map((m, i) => (
            <div key={i} style={{ padding: "12px 14px", borderRadius: 8, background: C.card, border: `1px solid ${m.color}18` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: m.color + "18", border: `1.5px solid ${m.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: m.color, flexShrink: 0 }}>{m.step}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: m.color }}>{m.title}</div>
              </div>
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5 }}>{m.desc}</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function AlignmentView({ activities, alignmentMatrix }) {
  const am = alignmentMatrix;
  return (
    <div>
      <SectionCard title="ALIGNMENT ASSESSMENT FRAMEWORK" subtitle="Three conditions per Art. 3 of the Taxonomy Regulation" icon={"🎯"} color={C.purple} badge="Art. 3">
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { label: "Substantial Contribution", desc: "Activity must make a substantial contribution to at least ONE of the six environmental objectives (Art. 10–15)", icon: "🎯", color: C.emerald, articles: ["Art. 10", "Art. 11", "Art. 12", "Art. 13", "Art. 14", "Art. 15"] },
            { label: "Do No Significant Harm (DNSH)", desc: "Activity must not significantly harm ANY of the other five environmental objectives (Art. 17)", icon: "🛡️", color: C.amber, articles: ["Art. 16", "Art. 17"] },
            { label: "Minimum Safeguards", desc: "Company must comply with OECD Guidelines for MNEs, UN Guiding Principles, ILO Core Conventions, International Bill of Human Rights (Art. 18)", icon: "⚖️", color: C.blue, articles: ["Art. 18"] },
          ].map((c, i) => (
            <div key={i} style={{ flex: 1, minWidth: 200, padding: "14px 16px", borderRadius: 9, background: C.card, border: `1px solid ${c.color}22` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 18 }}>{c.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{c.label}</span>
              </div>
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>{c.desc}</div>
              <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>{c.articles.map(a => <RegRef key={a} article={a} />)}</div>
            </div>
          ))}
        </div>
      </SectionCard>

      {am && (
        <SectionCard title="DETAILED ALIGNMENT — SAMPLE ACTIVITY" subtitle={am.activity} icon={"☀️"} color={C.emerald} badge={am.nace}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.emerald, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              {"🎯"} SUBSTANTIAL CONTRIBUTION {"—"} Climate Change Mitigation
              {am.sc?.met && <Badge text="MET" color={C.emerald} />}
            </div>
            <div style={{ padding: "10px 14px", borderRadius: 7, background: C.card, border: `1px solid ${C.emerald}22` }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 10 }}>
                <div><span style={{ color: C.muted }}>Criterion:</span><div style={{ color: C.text, marginTop: 2 }}>{am.sc?.criteria}</div></div>
                <div><span style={{ color: C.muted }}>Threshold:</span><div style={{ color: C.amber, fontWeight: 600, marginTop: 2 }}>{am.sc?.threshold}</div></div>
                <div><span style={{ color: C.muted }}>Evidence:</span><div style={{ color: C.emerald, marginTop: 2 }}>{am.sc?.evidence}</div></div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              {"🛡️"} DO NO SIGNIFICANT HARM {"—"} All 5 Remaining Objectives
              <Badge text="ALL MET" color={C.emerald} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {(am.dnsh || []).map((d, i) => (
                <div key={i} style={{ padding: "8px 12px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "150px 1fr 1fr 60px", gap: 10, alignItems: "center", fontSize: 10 }}>
                  <span style={{ fontWeight: 600, color: C.text }}>{d.objective}</span>
                  <span style={{ color: C.muted }}>{d.criteria}</span>
                  <span style={{ color: C.text }}>{d.evidence}</span>
                  <span style={{ textAlign: "right" }}>{d.met ? <span style={{ color: C.emerald, fontWeight: 700 }}>{"✓"} PASS</span> : <span style={{ color: C.red, fontWeight: 700 }}>{"✗"} FAIL</span>}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              {"⚖️"} MINIMUM SAFEGUARDS
              {am.safeguards?.met && <Badge text="COMPLIANT" color={C.emerald} />}
            </div>
            <div style={{ padding: "10px 14px", borderRadius: 7, background: C.card, border: `1px solid ${C.blue}22`, fontSize: 10, color: C.text, lineHeight: 1.5 }}>
              {am.safeguards?.evidence}
            </div>
            <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
              <RegRef article="Art. 18" />
              {["OECD MNE Guidelines", "UNGP", "ILO Core Conventions", "IBHR"].map(s => (
                <Pill key={s} color={C.blue} small>{s}</Pill>
              ))}
            </div>
          </div>
        </SectionCard>
      )}

      <SectionCard title="ALIGNMENT DECISION MATRIX — ALL ACTIVITIES" subtitle="Summary view across portfolio" icon={"📊"} color={C.purple}>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="alignment-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr>
                {["Activity", "NACE", "SC Objective", "SC Met?", "DNSH Met?", "Min. Safeguards?", "ALIGNED?"].map((h, i) => (
                  <th key={i} style={{ padding: "7px 10px", textAlign: "left", borderBottom: `1px solid ${C.borderHi}`, color: C.muted, fontWeight: 600, fontSize: 9, letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activities.filter(a => a.eligible).map((a, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "7px 10px", color: C.text, maxWidth: 200, whiteSpace: "normal", lineHeight: 1.3 }}>{a.tax_activity}</td>
                  <td style={{ padding: "7px 10px", fontFamily: "monospace", color: C.cyan, fontWeight: 600 }}>{a.nace}</td>
                  <td style={{ padding: "7px 10px" }}>{(() => { const o = ENV_OBJECTIVES.find(o => o.id === a.objective); return o ? <Pill color={o.color} small>{o.icon} {o.label}</Pill> : "—"; })()}</td>
                  <td style={{ padding: "7px 10px" }}>{a.aligned === true ? <span style={{ color: C.emerald }}>{"✓"}</span> : a.aligned === "review" ? <span style={{ color: C.amber }}>{"⏳"}</span> : <span style={{ color: C.red }}>{"✗"}</span>}</td>
                  <td style={{ padding: "7px 10px" }}>{a.aligned === true ? <span style={{ color: C.emerald }}>{"✓"}</span> : a.aligned === "review" ? <span style={{ color: C.amber }}>{"⏳"}</span> : <span style={{ color: C.dim }}>{"—"}</span>}</td>
                  <td style={{ padding: "7px 10px" }}><span style={{ color: C.emerald }}>{"✓"}</span></td>
                  <td style={{ padding: "7px 10px", fontWeight: 700 }}>
                    {a.aligned === true ? <span style={{ color: C.emerald, background: C.emerald + "15", padding: "2px 8px", borderRadius: 4 }}>{"✓"} ALIGNED</span>
                      : a.aligned === "review" ? <span style={{ color: C.amber, background: C.amber + "15", padding: "2px 8px", borderRadius: 4 }}>{"⏳"} REVIEW</span>
                      : <span style={{ color: C.red, background: C.red + "15", padding: "2px 8px", borderRadius: 4 }}>{"✗"} NOT ALIGNED</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

function KPIView({ activities, kpis }) {
  const eligible = activities.filter(a => a.eligible);
  const totals = kpis?.totals || { turnover: 1, capex: 1, opex: 1 };
  const eligK = kpis?.eligible || { turnover: 0, capex: 0, opex: 0 };
  const alignK = kpis?.aligned || { turnover: 0, capex: 0, opex: 0 };

  const KPITable = ({ label, denom, eligNum, alignNum, color }) => (
    <div style={{ flex: 1, minWidth: 280 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 8, letterSpacing: 0.3 }}>{label}</div>
      <div style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <tbody>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: "8px 12px", color: C.muted, background: C.card }}>Total {label} (Denominator)</td>
              <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.text, background: C.card }}>{fmtEur(denom)}</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: "8px 12px", color: C.muted }}>Taxonomy-Eligible (Numerator A)</td>
              <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", color: C.text }}>{fmtEur(eligNum)}</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: "8px 12px", color: C.muted }}>Taxonomy-Aligned (Numerator B)</td>
              <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", color: C.emerald, fontWeight: 600 }}>{fmtEur(alignNum)}</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${C.border}`, background: color + "08" }}>
              <td style={{ padding: "8px 12px", color, fontWeight: 700 }}>Eligibility Ratio</td>
              <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", color, fontWeight: 800, fontSize: 14 }}>{denom > 0 ? fmtPct(eligNum / denom) : "0.0%"}</td>
            </tr>
            <tr style={{ background: color + "12" }}>
              <td style={{ padding: "8px 12px", color, fontWeight: 700 }}>Alignment Ratio</td>
              <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", color, fontWeight: 800, fontSize: 14 }}>{denom > 0 ? fmtPct(alignNum / denom) : "0.0%"}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <SectionCard title="KPI CALCULATION ENGINE" subtitle="Turnover, CapEx, OpEx per Art. 8 & Disclosures Delegated Act" icon={"📊"} color={C.amber} badge="Art. 8">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <KPITable label="Turnover" denom={totals.turnover} eligNum={eligK.turnover} alignNum={alignK.turnover} color={C.emerald} />
          <KPITable label="CapEx" denom={totals.capex} eligNum={eligK.capex} alignNum={alignK.capex} color={C.blue} />
          <KPITable label="OpEx" denom={totals.opex} eligNum={eligK.opex} alignNum={alignK.opex} color={C.amber} />
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <RegRef article="Art. 8" /> <RegRef article="Del. Reg. 2021/2178" />
        </div>
      </SectionCard>

      <SectionCard title="KPI METHODOLOGY — DENOMINATOR DEFINITIONS" subtitle="Per Del. Reg. 2021/2178 (Disclosures Delegated Act)" icon={"📐"} color={C.purple}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { label: "TURNOVER", def: "Net turnover derived from products or services associated with Taxonomy-eligible economic activities (numerator) divided by total net turnover (denominator) as defined in Article 2(5) of Directive 2013/34/EU.", color: C.emerald, formula: "Taxonomy-Eligible Revenue / Total Net Revenue", source: "Income Statement — Revenue line per IFRS 15 / IAS 18" },
            { label: "CAPEX", def: "Additions to tangible and intangible assets during the financial year before depreciation, amortisation and re-measurements. Includes IAS 16 Property Plant & Equipment, IAS 38 Intangible Assets, IFRS 16 Right-of-use Assets, IAS 40 Investment Property, IAS 41 Biological Assets, and IFRS 3 Business Combinations.", color: C.blue, formula: "Taxonomy-Eligible CapEx / Total CapEx (IAS 16 + IAS 38 + IFRS 16 additions)", source: "Notes to Financial Statements — PP&E, Intangibles, Lease additions" },
            { label: "OPEX", def: "Direct non-capitalised costs relating to: research and development, building renovation measures, short-term lease, maintenance and repair, and any other direct expenditures relating to the day-to-day servicing of assets of property, plant and equipment.", color: C.amber, formula: "Taxonomy-Eligible OpEx / Total OpEx (R&D + renovation + maintenance + short-term leases)", source: "P&L — R&D expense, Maintenance, Repair, Short-term leases" },
          ].map((m, i) => (
            <div key={i} style={{ padding: "12px 14px", borderRadius: 8, background: C.card, border: `1px solid ${m.color}22` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: m.color, marginBottom: 6 }}>{m.label} KPI</div>
              <div style={{ fontSize: 10, color: C.text, lineHeight: 1.5, marginBottom: 6 }}>{m.def}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ padding: "6px 10px", borderRadius: 5, background: C.surface, fontSize: 9 }}>
                  <span style={{ color: C.muted }}>Formula: </span><span style={{ color: m.color, fontFamily: "monospace", fontWeight: 600 }}>{m.formula}</span>
                </div>
                <div style={{ padding: "6px 10px", borderRadius: 5, background: C.surface, fontSize: 9 }}>
                  <span style={{ color: C.muted }}>Data Source: </span><span style={{ color: C.text }}>{m.source}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="ACTIVITY-LEVEL KPI BREAKDOWN" subtitle="Numerator contribution per eligible activity" icon={"📋"} color={C.teal}>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="kpi-breakdown-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr>
                {["Activity", "NACE", "Aligned?", "Turnover", "% of Total", "CapEx", "% of Total", "OpEx", "% of Total"].map((h, i) => (
                  <th key={i} style={{ padding: "7px 10px", textAlign: i > 2 ? "right" : "left", borderBottom: `1px solid ${C.borderHi}`, color: C.muted, fontWeight: 600, fontSize: 9, letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {eligible.map((a, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "7px 10px", color: C.text, maxWidth: 200, whiteSpace: "normal", lineHeight: 1.3 }}>{a.tax_activity}</td>
                  <td style={{ padding: "7px 10px", fontFamily: "monospace", color: C.cyan }}>{a.nace}</td>
                  <td style={{ padding: "7px 10px" }}>{a.aligned === true ? <span style={{ color: C.emerald }}>{"✓"}</span> : a.aligned === "review" ? <span style={{ color: C.amber }}>{"⏳"}</span> : <span style={{ color: C.red }}>{"✗"}</span>}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: "monospace" }}>{fmtEur(a.turnover)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: "monospace", color: C.emerald }}>{totals.turnover > 0 ? fmtPct(a.turnover / totals.turnover) : "0.0%"}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: "monospace" }}>{fmtEur(a.capex)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: "monospace", color: C.blue }}>{totals.capex > 0 ? fmtPct(a.capex / totals.capex) : "0.0%"}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: "monospace" }}>{fmtEur(a.opex)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: "monospace", color: C.amber }}>{totals.opex > 0 ? fmtPct(a.opex / totals.opex) : "0.0%"}</td>
                </tr>
              ))}
              <tr style={{ background: C.card }}>
                <td colSpan={3} style={{ padding: "8px 10px", fontWeight: 700, color: C.text }}>TOTAL ELIGIBLE</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.text }}>{fmtEur(eligK.turnover)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.emerald }}>{totals.turnover > 0 ? fmtPct(eligK.turnover / totals.turnover) : "0.0%"}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.text }}>{fmtEur(eligK.capex)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.blue }}>{totals.capex > 0 ? fmtPct(eligK.capex / totals.capex) : "0.0%"}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.text }}>{fmtEur(eligK.opex)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: C.amber }}>{totals.opex > 0 ? fmtPct(eligK.opex / totals.opex) : "0.0%"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

function WorkflowView({ workflowSteps }) {
  const [expandedStep, setExpandedStep] = useState(1);

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>EU Taxonomy Implementation Workflow</div>
        <div style={{ fontSize: 11, color: C.muted }}>6-step process from scoping to disclosure {"—"} mapped to regulations</div>
      </div>

      {workflowSteps.map((step, si) => {
        const isExpanded = expandedStep === step.num;
        const doneTasks = (step.tasks || []).filter(t => t.status === "done").length;
        const totalTasks = (step.tasks || []).length;
        const pct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
        const meta = STEP_META[step.step_id] || { icon: "", color: C.muted };

        return (
          <div key={step.step_id}>
            <div data-testid={`workflow-step-${step.step_id}`} onClick={() => setExpandedStep(isExpanded ? null : step.num)} style={{
              padding: "14px 18px", borderRadius: isExpanded ? "10px 10px 0 0" : 10,
              background: isExpanded ? meta.color + "08" : C.card,
              border: `1px solid ${isExpanded ? meta.color + "44" : C.border}`,
              borderBottom: isExpanded ? "none" : undefined,
              cursor: "pointer", transition: "all 0.2s", marginBottom: isExpanded ? 0 : 4,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: meta.color + "18", border: `2px solid ${meta.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: meta.color, flexShrink: 0 }}>{step.num}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 15 }}>{meta.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>{step.label}</span>
                    <span style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>"{step.question}"</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 3, maxWidth: 600, lineHeight: 1.4 }}>{step.desc}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: pct === 100 ? C.emerald : meta.color }}>{pct}%</div>
                    <div style={{ fontSize: 9, color: C.muted }}>{doneTasks}/{totalTasks} tasks</div>
                  </div>
                  <span style={{ fontSize: 11, color: C.dim, transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", display: "inline-block" }}>{"▼"}</span>
                </div>
              </div>
            </div>
            {isExpanded && (
              <div style={{ padding: "16px 18px", border: `1px solid ${meta.color}44`, borderTop: `1px solid ${meta.color}18`, borderRadius: "0 0 10px 10px", background: C.surface, marginBottom: 4 }}>
                <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 9, color: C.dim, letterSpacing: 0.8, marginRight: 4 }}>REGULATIONS:</span>
                  {(step.articles || []).map(a => <RegRef key={a} article={a} />)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {(step.tasks || []).map((task, ti) => (
                    <div key={ti} data-testid={`task-${task.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}` }}>
                      <StatusDot status={task.status} />
                      <span style={{ flex: 1, fontSize: 10, color: C.text }}>{task.label}</span>
                      <Pill color={C.amber} small>{"👤"} {task.owner}</Pill>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {si < workflowSteps.length - 1 && !isExpanded && (
              <div style={{ display: "flex", justifyContent: "center", padding: "0" }}>
                <svg width="18" height="14" viewBox="0 0 18 14"><path d="M9 0 L9 8 M5 5 L9 11 L13 5" fill="none" stroke={(STEP_META[workflowSteps[si + 1]?.step_id]?.color || C.dim) + "44"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RegulationView() {
  const [search, setSearch] = useState("");

  const groups = [
    { label: "Taxonomy Regulation (EU) 2020/852", color: C.blue, articles: ["Art. 1", "Art. 2", "Art. 3", "Art. 5", "Art. 8", "Art. 9"] },
    { label: "Substantial Contribution Criteria (Art. 10–15)", color: C.emerald, articles: ["Art. 10", "Art. 11", "Art. 12", "Art. 13", "Art. 14", "Art. 15"] },
    { label: "DNSH & Minimum Safeguards (Art. 16–18)", color: C.amber, articles: ["Art. 16", "Art. 17", "Art. 18"] },
    { label: "Delegated Regulations", color: C.purple, articles: ["Del. Reg. 2021/2139", "Del. Reg. 2022/1214", "Del. Reg. 2023/2486", "Del. Reg. 2021/2178"] },
  ];

  const WORKFLOW_STEPS_REF = [
    { id: "scope", num: 1, label: "Scope & Entities", color: C.cyan, articles: ["Art. 1", "Art. 8", "Del. Reg. 2021/2178"] },
    { id: "classify", num: 2, label: "Activity Classification", color: C.blue, articles: ["Art. 2", "Art. 9", "Del. Reg. 2021/2139", "Del. Reg. 2023/2486"] },
    { id: "eligible", num: 3, label: "Eligibility Assessment", color: C.emerald, articles: ["Art. 8", "Del. Reg. 2021/2139", "Del. Reg. 2022/1214", "Del. Reg. 2023/2486", "Del. Reg. 2021/2178"] },
    { id: "align", num: 4, label: "Alignment Assessment", color: C.purple, articles: ["Art. 3", "Art. 10", "Art. 11", "Art. 12", "Art. 13", "Art. 14", "Art. 15", "Art. 16", "Art. 17", "Art. 18"] },
    { id: "calculate", num: 5, label: "KPI Calculation", color: C.amber, articles: ["Art. 8", "Del. Reg. 2021/2178"] },
    { id: "disclose", num: 6, label: "Disclosure & Export", color: C.teal, articles: ["Art. 8", "Del. Reg. 2021/2178"] },
  ];

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <input data-testid="regulation-search" type="text" placeholder="Search regulations, articles, definitions..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 11, outline: "none", boxSizing: "border-box" }} />
      </div>

      {groups.map((g, gi) => (
        <SectionCard key={gi} title={g.label} icon={"📜"} color={g.color}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {g.articles.filter(a => {
              if (!search) return true;
              const reg = REGULATIONS[a];
              return a.toLowerCase().includes(search.toLowerCase()) || reg?.title.toLowerCase().includes(search.toLowerCase()) || reg?.desc.toLowerCase().includes(search.toLowerCase());
            }).map(articleKey => {
              const reg = REGULATIONS[articleKey];
              if (!reg) return null;
              const relatedSteps = WORKFLOW_STEPS_REF.filter(s => s.articles.includes(articleKey));
              return (
                <div key={articleKey} style={{ padding: "10px 14px", borderRadius: 7, background: C.card, border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: g.color, fontFamily: "monospace" }}>{articleKey}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{reg.title}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, marginBottom: 6 }}>{reg.desc}</div>
                  {relatedSteps.length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 8, color: C.dim, letterSpacing: 0.6 }}>USED IN:</span>
                      {relatedSteps.map(s => <Pill key={s.id} color={s.color} small>{STEP_META[s.id]?.icon} Step {s.num}: {s.label}</Pill>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────
export default function EUTaxonomyModule() {
  const [activeView, setActiveView] = useState("dashboard");
  const [activities, setActivities] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [alignmentMatrix, setAlignmentMatrix] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, actRes, wfRes, alignRes] = await Promise.all([
        fetch(`${API}/api/v1/taxonomy-compliance/dashboard`, { headers }),
        fetch(`${API}/api/v1/taxonomy-compliance/activities`, { headers }),
        fetch(`${API}/api/v1/taxonomy-compliance/workflow`, { headers }),
        fetch(`${API}/api/v1/taxonomy-compliance/alignment`, { headers }),
      ]);

      if (dashRes) {
        const dash = await dashRes.json();
        setKpis(dash.kpis);
        setConfig(dash.config || {});
      }
      if (actRes) {
        const actData = await actRes.json();
        setActivities(actData.activities || []);
      }
      if (wfRes) {
        const wfData = await wfRes.json();
        setWorkflowSteps(wfData.steps || []);
      }
      if (alignRes) {
        const alignData = await alignRes.json();
        setAlignmentMatrix(alignData);
      }
    } catch (err) {
      console.error("Failed to load taxonomy compliance data:", err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const views = [
    { id: "dashboard", label: "Dashboard", icon: "📊" },
    { id: "workflow", label: "Workflow", icon: "🔄" },
    { id: "classify", label: "Classification", icon: "🏷️" },
    { id: "alignment", label: "Alignment", icon: "🎯" },
    { id: "kpi", label: "KPI Engine", icon: "📈" },
    { id: "regulations", label: "Regulations", icon: "📜" },
  ];

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
        <div data-testid="loading-spinner" style={{ textAlign: "center" }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.blue}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 12 }}>Loading EU Taxonomy Module...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="eu-taxonomy-module" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "14px 20px 10px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 10, background: C.bg + "f0", backdropFilter: "blur(12px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 7, background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: C.white }}>D</div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>DisclosureOS</span>
                <span style={{ color: C.dim }}>/</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.emerald }}>EU Taxonomy</span>
              </div>
              <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase" }}>Regulation (EU) 2020/852 {"·"} Delegated Acts 2021{"–"}2023</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Pill color={C.emerald}>{config.reporting_period || "FY 2025"}</Pill>
            <Pill color={C.blue}>{config.entity_name || "Acme Corp Group"}</Pill>
            <Pill color={C.amber}>{config.entities_in_scope || 5} entities in scope</Pill>
          </div>
        </div>
        <div style={{ display: "flex", gap: 3, marginTop: 10, flexWrap: "wrap", overflowX: "auto" }}>
          {views.map(v => (
            <button key={v.id} data-testid={`tab-${v.id}`} onClick={() => setActiveView(v.id)} style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "5px 12px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer",
              border: `1px solid ${activeView === v.id ? C.blue : C.border}`,
              background: activeView === v.id ? C.blue + "15" : "transparent",
              color: activeView === v.id ? C.blue : C.muted,
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}><span>{v.icon}</span> {v.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "16px 20px 40px", maxWidth: 1320, margin: "0 auto" }}>
        {activeView === "dashboard" && <DashboardView activities={activities} kpis={kpis} workflowSteps={workflowSteps} config={config} />}
        {activeView === "workflow" && <WorkflowView workflowSteps={workflowSteps} />}
        {activeView === "classify" && <ClassificationView activities={activities} />}
        {activeView === "alignment" && <AlignmentView activities={activities} alignmentMatrix={alignmentMatrix} />}
        {activeView === "kpi" && <KPIView activities={activities} kpis={kpis} />}
        {activeView === "regulations" && <RegulationView />}
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "16px 20px 24px", borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 300, color: C.muted, fontStyle: "italic" }}>
          "Enter data once. <span style={{ color: C.emerald, fontWeight: 600 }}>Defend it everywhere.</span>"
        </div>
        <div style={{ fontSize: 9, color: C.dim, marginTop: 6 }}>
          EU Taxonomy Module {"·"} Regulation (EU) 2020/852 {"·"} Climate Delegated Act 2021/2139 {"·"} Environmental Delegated Act 2023/2486 {"·"} Disclosures Delegated Act 2021/2178
        </div>
      </div>
    </div>
  );
}
